import { Component } from '@theme/component';
import { debounce, getActiveHeaderSection, getStickyOffset, isMobileBreakpoint } from '@theme/utilities';

export class StickyContent extends Component {
  #prevWidth = 0;
  #resizeObserver = null;
  #scrollRaf = 0;
  #lastTargetHeight = 0;
  #lastReferenceHeight = 0;
  #lastScrollPosition = 0;
  #stickyTravel = 0;
  #scrollRoot = window;
  // null = not checked yet, NaN = checked & no override, number = override value
  #cachedStickyTopOverride = null;
  // Map<cssVarName, number | null> — invalidated on resize via #resetStickyCache
  #rootVarCache = new Map();
  // { scrollRoot, value } | null — invalidated when scrollRoot changes
  #cachedBottomOffset = null;
  #scrollingUp = false;
  #lastMaxTravel = 0;
  // Computed once per #setupSticky call
  #spacingTop = 20;
  #spacingBottom = 20;
  #marginTopOffset = 0;

  #handleObservedResize = debounce(() => {
    const target = this.#stickyTarget;
    const reference = this.#stickyReference;
    if (!target || !reference) return;

    const targetHeight = target.offsetHeight;
    const referenceHeight = reference.offsetHeight;
    if (targetHeight === this.#lastTargetHeight && referenceHeight === this.#lastReferenceHeight) return;

    this.#setupSticky();
  }, 50);

  #handleResize = debounce(() => {
    const width = window.innerWidth;
    if (width !== this.#prevWidth) {
      this.#prevWidth = width;
      this.#updateScrollListener();
      this.#setupSticky();
      return;
    }

    this.#applyStickyState();
  }, 50);

  #updateScrollListener() {
    if (isMobileBreakpoint()) {
      this.#scrollRoot.removeEventListener('scroll', this.#handleScroll);
    } else {
      this.#scrollRoot.addEventListener('scroll', this.#handleScroll, { passive: true });
    }
  }

  #resetStickyCache() {
    this.#cachedStickyTopOverride = null;
    this.#rootVarCache.clear();
    this.#cachedBottomOffset = null;
  }

  #handleScroll = () => {
    const currentPos = this.#scrollPosition;
    const eventDelta = currentPos - this.#lastScrollPosition;
    this.#lastScrollPosition = currentPos;
    this.#scrollingUp = eventDelta < 0;

    if (this.#lastMaxTravel > 0) {
      const next = this.#stickyTravel + eventDelta;
      this.#stickyTravel = Math.max(0, Math.min(next, this.#lastMaxTravel));
    }

    if (this.#scrollRaf) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = 0;
      this.#applyStickyState();
    });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#prevWidth = window.innerWidth;
    this.#scrollRoot = this.#resolvedScrollRoot;
    this.#lastScrollPosition = this.#scrollPosition;
    // Read static spacing up front so it is correct even when the first
    // #setupSticky() bails early (e.g. inside a hidden dialog, offsetHeight 0).
    this.#readSpacingConfig();

    window.addEventListener('resize', this.#handleResize);
    this.#updateScrollListener();

    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(this.#handleObservedResize);
    }

    this.#setupSticky();
  }

  #readSpacingConfig() {
    this.#spacingTop = this.#resolveSpacing(this.dataset.spacingTop, this.dataset.spacing);
    this.#spacingBottom = this.#resolveSpacing(this.dataset.spacingBottom, this.dataset.spacing);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.#scrollRoot.removeEventListener('scroll', this.#handleScroll);
    this.#handleResize.cancel?.();
    this.#handleObservedResize.cancel?.();

    if (this.#scrollRaf) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = 0;
    }

    this.#resizeObserver?.disconnect();
    this.#resetStickyCache();
  }

  #updateStickyPosition(sticky, mode, size, currentTop) {
    sticky.classList.add('sticky-content-active');
    if (sticky.dataset.stickyState !== mode) sticky.dataset.stickyState = mode;
    if (sticky.dataset.stickySize !== size) sticky.dataset.stickySize = size;
    sticky.style.setProperty('--sticky-content-current-top', `${currentTop}px`);
  }

  #setupSticky() {
    this.#resizeObserver?.disconnect();
    this.#resetStickyCache();

    const pair = this.#stickyPair;
    if (!pair) {
      this.#clearSticky();
      return;
    }

    const { target, reference, targetHeight, referenceHeight, sticky, stickyHeight } = pair;

    this.#lastTargetHeight = targetHeight;
    this.#lastReferenceHeight = referenceHeight;
    this.#readSpacingConfig();
    this.#marginTopOffset = this.#computeMarginTopOffset(sticky);

    const maxTravel = this.#getMaxStickyTravel(stickyHeight);
    this.#stickyTravel = Math.max(0, Math.min(this.#stickyTravel, maxTravel));

    this.#resizeObserver?.observe(target);
    this.#resizeObserver?.observe(reference);
    this.#applyStickyState();
  }

  #getMaxStickyTravel(stickyHeight) {
    return Math.max(0, this.#stickyTop - this.#getStickyBottomTop(stickyHeight));
  }

  #resolveOversizedMode(maxTravel) {
    if (this.#stickyTravel <= 0) return 'top';
    if (this.#stickyTravel >= maxTravel) return 'bottom';
    return 'free';
  }

  #clearStickyStyles(element) {
    element?.classList.remove('sticky-content-active');
    if (element) {
      element.removeAttribute('data-sticky-state');
      element.removeAttribute('data-sticky-size');
    }
    element?.style.removeProperty('--sticky-content-current-top');
  }

  #getStickyBottomTop(stickyHeight) {
    return this.#viewportHeight - stickyHeight - this.#spacingBottom - this.#stickyBottomOffset;
  }

  #applyStickyState() {
    const pair = this.#stickyPair;
    if (!pair) {
      this.#clearSticky();
      return;
    }

    const { sticky, other, stickyHeight } = pair;
    this.#marginTopOffset = this.#computeMarginTopOffset(sticky);

    if (stickyHeight <= this.#availableHeight) {
      this.#stickyTravel = 0;
      this.#lastMaxTravel = 0;
      this.#updateStickyPosition(sticky, 'top', 'fit', this.#stickyTop);
    } else {
      const maxTravel = this.#getMaxStickyTravel(stickyHeight);
      this.#lastMaxTravel = maxTravel;
      if (maxTravel <= 0) {
        this.#stickyTravel = 0;
        this.#updateStickyPosition(sticky, 'top', 'oversized', this.#stickyTop);
      } else {
        this.#stickyTravel = Math.max(0, Math.min(this.#stickyTravel, maxTravel));
        const mode = this.#resolveOversizedMode(maxTravel);
        const currentTop = mode === 'bottom' ? this.#getStickyBottomTop(stickyHeight)
          : mode === 'free' ? this.#stickyTop - this.#stickyTravel
          : this.#stickyTop;
        this.#updateStickyPosition(sticky, mode, 'oversized', currentTop);
      }
    }

    this.#clearStickyStyles(other);
  }

  #clearSticky() {
    this.#stickyTravel = 0;
    this.#clearStickyStyles(this.#stickyTarget);
    this.#clearStickyStyles(this.#stickyReference);
  }

  get #stickyPair() {
    const target = this.#stickyTarget;
    const reference = this.#stickyReference;
    if (!this.#isStickyEnabled || !target || !reference || isMobileBreakpoint()) return null;

    const targetHeight = target.offsetHeight;
    const referenceHeight = reference.offsetHeight;
    if (!targetHeight || !referenceHeight || targetHeight === referenceHeight) return null;

    const targetIsSticky = targetHeight < referenceHeight;
    const sticky = targetIsSticky ? target : reference;
    const other = targetIsSticky ? reference : target;
    const stickyHeight = targetIsSticky ? targetHeight : referenceHeight;

    return { target, reference, targetHeight, referenceHeight, sticky, other, stickyHeight };
  }

  get #resolvedScrollRoot() {
    const selector = this.dataset.scrollRoot;
    return selector ? this.closest(selector) || window : window;
  }

  get #scrollPosition() {
    return this.#scrollRoot === window ? window.scrollY : this.#scrollRoot.scrollTop;
  }

  get #viewportHeight() {
    return this.#scrollRoot === window ? window.innerHeight : this.#scrollRoot.clientHeight;
  }

  get #stickyTopOffset() {
    const override = this.#getStickyTopOverride();
    if (override !== null) return override;
    return this.#getStickyHeaderHeight() ?? getStickyOffset();
  }

  #getStickyTopOverride() {
    if (this.#cachedStickyTopOverride === null) {
      const value = getComputedStyle(this).getPropertyValue('--sticky-content-top').trim();
      this.#cachedStickyTopOverride = value ? Number.parseFloat(value) : NaN;
    }
    return Number.isNaN(this.#cachedStickyTopOverride) ? null : this.#cachedStickyTopOverride;
  }

  #getStickyHeaderHeight() {
    const header = getActiveHeaderSection();
    if (!header) return null;
    const { classList } = header;

    if (classList.contains('header-sticky--scroll_up') && classList.contains('header-pinning')) {
      return this.#readRootVar('--header-scrollup-height');
    }
    if (this.#scrollingUp && (classList.contains('header-sticky--top_only') || classList.contains('header-sticky--bottom_only'))) {
      return this.#readRootVar('--header-height');
    }
    return null;
  }

  #readRootVar(name) {
    const cache = this.#rootVarCache;
    if (cache.has(name)) return cache.get(name);
    const parsed = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    const value = Number.isNaN(parsed) ? null : parsed;
    cache.set(name, value);
    return value;
  }

  #resolveSpacing(...values) {
    for (const value of values) {
      if (!value) continue;
      const parsed = Number.parseFloat(value.trim());
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 20;
  }

  #computeMarginTopOffset(stickyEl) {
    const selector = this.dataset.marginTarget;
    const element = selector ? this.querySelector(selector) : null;
    if (!element || element !== stickyEl) return 0;
    const marginTop = Number.parseFloat(getComputedStyle(element).marginTop);
    return !Number.isNaN(marginTop) && marginTop < 0 ? Math.abs(marginTop) : 0;
  }

  get #stickyTop() {
    return this.#stickyTopOffset + this.#spacingTop - this.#marginTopOffset;
  }

  get #availableHeight() {
    return this.#viewportHeight - this.#stickyTop - this.#spacingBottom;
  }

  get #stickyBottomOffset() {
    const scrollRoot = this.#scrollRoot;
    if (scrollRoot === window) return 0;

    const cached = this.#cachedBottomOffset;
    if (cached && cached.scrollRoot === scrollRoot) return cached.value;

    const parsed = Number.parseFloat(getComputedStyle(scrollRoot).paddingBottom);
    const value = Number.isNaN(parsed) ? 0 : parsed;
    this.#cachedBottomOffset = { scrollRoot, value };
    return value;
  }

  get #isStickyEnabled() {
    return this.dataset.enabled === 'true';
  }

  get #stickyTarget() {
    const selector = this.dataset.primary || '.sticky-content__primary';
    return this.querySelector(selector);
  }

  get #stickyReference() {
    const selector = this.dataset.secondary || '.sticky-content__secondary';
    return this.querySelector(selector);
  }
}

if (!customElements.get('sticky-content')) {
  customElements.define('sticky-content', StickyContent);
}
